import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';
import classNames from 'classnames';
import Card from '@helios/Card';
import Container from '@helios/Container';
import FAQAccordion from '@helios/FAQAccordion';
import FAQSection from '@helios/FAQSection';
import Grid from '@helios/Grid';
import PageHero from '@helios/PageHero';
import Section from '@helios/Section';
import Layout from '@/components/Layout.js';
import useScroll from '@/hooks/useScroll.js';
import { COST } from '@/utils/programCosts.js';
import LeadFormLink from '@/components/LeadFormLink';
import SolidFileArrowDownIcon from '@/components/icons/solid/file-arrow-down';
import SolidMapIcon from '@/components/icons/solid/map';
import ThinArrowDownLongIcon from '@/components/icons/thin/arrow-down-long';
import VisitBanner from '@/public/images/visit-banner.jpg';
import SacramentoLocation from '@/public/images/sacramento-location.jpg';
import SacramentoMap from '@/public/images/sacramento-map.jpg';
import faqVector from '@/public/images/faqvector.svg';
import heroContentStyles from '@/styles/PageHeroContent.module.scss';
import styles from '@/styles/SacramentoPage.module.scss';

// The rendered widths: the tour photo in its half of the container from md;
// the campus map in its 8-of-12 column of the card from xl.
const TOUR_IMAGE_SIZES = '(max-width: 767px) calc(100vw - 40px), 50vw';
const MAP_IMAGE_SIZES = '(max-width: 1199px) calc(100vw - 104px), calc((100vw - 208px) * 2 / 3)';
function ScrollButton(t0) {
  const $ = _c(6);
  const {
    children,
    onClick
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span>{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ThinArrowDownLongIcon className={styles["scroll-button__icon"]} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClick || $[4] !== t1) {
    t3 = <button type="button" className={styles["scroll-button"]} onClick={onClick}>{t1}{t2}</button>;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function Visit() {
  const $ = _c(39);
  const {
    scrollTo
  } = useScroll();
  const communityRef = useRef(null);
  let t0;
  if ($[0] !== scrollTo) {
    t0 = () => scrollTo(communityRef.current);
    $[0] = scrollTo;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const scrollToCommunity = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Head><title>Visit | Campus Sacramento | Formerly MTI College</title><link rel="canonical" href="https://sacramento.campus.edu/visit" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Campus, formerly MTI College, welcomes all visitors to our Sacramento, CA campus. Tour our campus, see classes in action, and learn more about our programs." /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h1 className={classNames(heroContentStyles.title, heroContentStyles["title--feature"], heroContentStyles["title--inverse"])}>Visit our Sacramento campus.</h1>;
    t3 = <div className={classNames(heroContentStyles.body, heroContentStyles["body--inverse"])}>Campus, formerly MTI College, welcomes all visitors to our Sacramento campus. See our campus, see classes in action, and learn more about our programs.</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== scrollToCommunity) {
    t4 = <PageHero.Content placement="split" tone="inverse">{t2}{t3}<div className={styles["scroll-desktop"]}><ScrollButton onClick={scrollToCommunity}>Schedule a tour</ScrollButton></div></PageHero.Content>;
    $[5] = scrollToCommunity;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PageHero.Media placement="split"><PageHero.Image src={VisitBanner} alt="A community for ambitious students to start college" frame="portrait" priority={true} /></PageHero.Media>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = <PageHero.Split>{t4}{t5}</PageHero.Split>;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== scrollToCommunity) {
    t7 = <div className={styles["scroll-mobile"]}><ScrollButton onClick={scrollToCommunity}>Experience campus</ScrollButton></div>;
    $[10] = scrollToCommunity;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <PageHero surface="dark"><PageHero.Container width="full">{t6}{t7}</PageHero.Container></PageHero>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      base: 1,
      md: 2
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <a href="https://maps.app.goo.gl/CUg4JcSgVzH5HwZ99"><Image src={SacramentoLocation} alt="Link to Google Maps for 5221 Madison Avenue in Sacramento" sizes={TOUR_IMAGE_SIZES} className={styles["fluid-image"]} /></a>;
    t11 = <p className={styles["section-title"]}>Schedule a campus tour</p>;
    t12 = <p>Campus tours are available Monday through Thursday from 8:00 am to 6:30 pm and on Friday from 8:00 am to 4:00 pm.</p>;
    t13 = <p>Campus is located at 5221 Madison Avenue in Sacramento. We are conveniently located near the Madison Avenue exit of Highway 80.</p>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <a href="tel:916 339-1500">(916) 339-1500</a>;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Section><Container><Grid columns={t9} gap="lg">{t10}<div>{t11}{t12}{t13}<p>Schedule an informative tour by calling us at{" "}{t14} or completing a{" "}<LeadFormLink>request form</LeadFormLink>. A representative of Campus will confirm your tour date as soon as your request is received.</p></div></Grid></Container></Section>;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      base: 1,
      xl: 12
    };
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      base: 1,
      xl: 4
    };
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  let t19;
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className={styles["visit-community__icon"]}><SolidMapIcon /></span>;
    t19 = <h2 className={styles["band-title"]}>Explore our campus.</h2>;
    t20 = <p>Campus Sacramento hosts a variety of facilities catered to each of our programs. We host classes in three different buildings: the Main Building, the North Annex, and Paul Mitchell The School. Find your way around the campus with our Campus Map.</p>;
    $[24] = t18;
    $[25] = t19;
    $[26] = t20;
  } else {
    t18 = $[24];
    t19 = $[25];
    t20 = $[26];
  }
  let t21;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Grid.Item span={t17}>{t18}{t19}{t20}<ul className={styles["doc-list"]}><li><a href="/documents/Campus_Map_Sacramento.pdf" target="_blank" id="Cosmetology"><SolidFileArrowDownIcon className={styles["doc-list__icon"]} />{" "}Download our campus map (PDF)</a></li></ul></Grid.Item>;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Section><div ref={communityRef}><Container><Card surface="subtle" padded="lg" mobilePadded="md"><Grid columns={t16} gap="lg">{t21}<Grid.Item span={{
                base: 1,
                xl: 8
              }}><Image src={SacramentoMap} alt="Find your community" sizes={MAP_IMAGE_SIZES} className={styles["fluid-image"]} /></Grid.Item></Grid></Card></Container></div></Section>;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      base: 1,
      lg: 2
    };
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <p className={styles["section-title"]}>Questions people often ask us:</p>;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div>{t24}<div className={styles["faq-art"]}><Image src={faqVector} alt="Image" sizes="457px" /></div></div>;
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  let t26;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      question: "Is Campus accredited?",
      answer: <><p>Yes, Campus is an accredited college. Campus is accredited by the Accrediting Commission for Community and Junior Colleges (ACCJC), an institutional accrediting body recognized by the Council for Higher Education Accreditation and the U.S. Department of Education. Additional information about accreditation, including the filing of complaints against member institutions, can be found at{" "}<a href="https://accjc.org/" target="_blank" rel="noreferrer">www.accjc.org</a>{"."}</p></>
    };
    $[32] = t26;
  } else {
    t26 = $[32];
  }
  let t27;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      question: "What is the cost of tuition?",
      answer: <><p>Tuition for all online Campus Scholars programs is{" "}{COST.annualTuition} per academic year.{" "}<Link href="/tuition">Learn more about tuition for the program</Link>{" "}and for options to potentially attend with no out-of-pocket costs for tuition.</p></>
    };
    $[33] = t27;
  } else {
    t27 = $[33];
  }
  let t28;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      question: "Am I able to use federal financial aid?",
      answer: <><p>Our programs are eligible for federal financial aid to those who qualify. To learn more, check out our page on <Link href="/tuition">tuition</Link>.</p></>
    };
    $[34] = t28;
  } else {
    t28 = $[34];
  }
  let t29;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      question: "How do I apply?",
      answer: <><p>Applicants are required to fill out a short{" "}<Link href="/admissions-application">application</Link>{" "}and complete an online interview with a member of our admissions team. Submissions of standardized test scores are not required.</p></>
    };
    $[35] = t29;
  } else {
    t29 = $[35];
  }
  let t30;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <FAQSection><Container><Grid columns={t23} gap="lg">{t25}<div><FAQAccordion items={[t26, t27, t28, t29, {
              question: "Why is it called Campus?",
              answer: <><p>Because we're creating a new model for the college experience, we wanted a name that would both encapsulate and stand out from the thousands of other colleges and universities in the country.</p></>
            }]} /></div></Grid></Container></FAQSection>;
    $[36] = t30;
  } else {
    t30 = $[36];
  }
  let t31;
  if ($[37] !== t8) {
    t31 = <Layout site="sacramento"><div className={styles.page}>{t1}{t8}{t15}{t22}{t30}</div></Layout>;
    $[37] = t8;
    $[38] = t31;
  } else {
    t31 = $[38];
  }
  return t31;
}
export default Visit;